import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import NegotiationSession from '@/components/Room/NegotiationSession'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { InviteMemberModal } from '@/components/Room/InviteStudentModal'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import { getRoomDetails, getRoomMembers } from '@/redux/asyncThunks/room'
import { GameRoomStatus } from '@/redux/types/room'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import AdvisorChatMonitoring from '@/components/Room/AdvisorChatMonitoring'

import ROUTES from '@/routes/routes'
import { fetchTimerConfigurationAPIThunk } from '@/redux/asyncThunks/timerConfiguration'

import RoomNotFound from '@/components/PageNotFound/RoomNotFound'
import { ArrowLeftCircle } from 'lucide-react'
import TeamChats from '@/components/Room/TeamChats'

const Monitoring: React.FC = () => {
  const params = useParams()
  const navigate = useNavigate()

  const dispatch = useDispatch<AppDispatch>()

  const { currentRoom, error } = useSelector((state: RootState) => state.room)

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const roomId = params.roomId as string

  const fetchRoomData = useCallback(
    async (isPollingCall = false) => {
      if (roomId) {
        try {
          await Promise.all([
            dispatch(getRoomDetails(roomId)),
            dispatch(getRoomMembers(roomId)),
          ])
        } catch (error) {
          console.error(
            `Failed to ${isPollingCall ? 'poll' : 'fetch'} room data:`,
            error
          )
        }
      }
    },
    [dispatch, roomId]
  )
  useEffect(() => {
    if (roomId) {
      dispatch(fetchTimerConfigurationAPIThunk(roomId))
    }
  }, [dispatch, roomId])

  useEffect(() => {
    fetchRoomData(false)
  }, [roomId])

  if (!currentRoom) {
    return (
      <div>
        <RoomNotFound message='Unable to fetch the room.' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center w-full h-full text-center'>
        <RoomNotFound message='Unable to fetch the details.' />
      </div>
    )
  }

  if (!currentRoom) {
    return null
  }

  const handleRoute = () => {
    navigate(ROUTES.PROFESSOR.ROOM_ADJUDICATE(roomId))
  }

  return (
    <div className='w-full px-24 pb-10'>
      <div className='mt-6 space-y-5'>
        <div className='flex items-center gap-x-2'>
          <NavLink
            to={ROUTES.PROFESSOR.ROOM_DETAILS(roomId)}
            className='flex-shrink-0'
          >
            <ArrowLeftCircle className='text-dark-blue hover:text-dark-blue-hovered' />
          </NavLink>
          {currentRoom?.status === GameRoomStatus.NOT_STARTED ? (
            <h1 className='text-2xl font-bold text-dark-blue'>
              Game Not Started
            </h1>
          ) : (
            <h1 className='text-2xl font-bold text-dark-blue'>
              Adjudicate Decision
            </h1>
          )}
        </div>

        {currentRoom?.status === GameRoomStatus.NOT_STARTED ? (
          <p className='text-lg text-black'>
            The game has not started yet. Please wait for the game to begin
            before monitoring and adjudicating decisions.
          </p>
        ) : (
          <>
            <p className='text-lg text-black'>
              Monitor the game progress and begin adjudicating the student
              decision.
            </p>
            <Button
              className='bg-dark-blue hover:bg-dark-blue-hovered'
              onClick={handleRoute}
            >
              <div className='flex items-center gap-2'>
                <img
                  src='/icons/adjudicate-white.svg'
                  alt='Start Game'
                  className='h-[1.13rem] w-[1.2rem] text-white'
                />
                <span className='text-base text-white'>
                  Adjudicate Decision
                </span>
              </div>
            </Button>
          </>
        )}
      </div>
      {currentRoom?.status !== GameRoomStatus.NOT_STARTED && (
        <>
          {/* Team panel is only meaningful in Team Mode (#549). */}
          {currentRoom?.game_mode === TimerMode.TEAM_MODE && <TeamChats />}
          <NegotiationSession />
          <AdvisorChatMonitoring />
        </>
      )}
      <InviteMemberModal
        canInviteProfessors={true}
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />
    </div>
  )
}

export default Monitoring
